import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { authFetch } from '@/api/http-client';
import { AuthShell } from '@/features/auth/components/AuthShell';
import { ROTAS } from '@/shared/lib/rotas';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const schema = z.object({ email: z.email('E-mail inválido') });
type Formulario = z.infer<typeof schema>;

export function EsqueciSenhaPage() {
  const [enviado, setEnviado] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema), defaultValues: { email: '' } });

  /**
   * ⚠️ Mostra sucesso **mesmo quando o e-mail não existe**, e o `catch` é vazio
   * de propósito.
   *
   * Responder "este e-mail não está cadastrado" transforma esta tela num
   * oráculo de contas: qualquer um descobre quais veterinários usam a
   * plataforma testando endereços. O servidor também não distingue — as duas
   * respostas são iguais dos dois lados.
   */
  async function aoEnviar(valores: Formulario): Promise<void> {
    try {
      await authFetch('/request-password-reset', {
        email: valores.email,
        redirectTo: `${window.location.origin}${ROTAS.redefinirSenha}`,
      });
    } catch {
      // Silêncio proposital — ver acima.
    }
    setEnviado(true);
  }

  if (enviado) {
    return (
      <AuthShell descricao="Verifique seu e-mail">
        <div className="space-y-4">
          <Alert className="bg-brand/10 border-brand/20">
            <Mail className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Se houver uma conta para <strong>{getValues('email')}</strong>, o link de
              redefinição chega em instantes. Ele vale por 1 hora.
            </AlertDescription>
          </Alert>

          <Button asChild variant="outline" className="w-full">
            <Link to={ROTAS.login}>
              <ArrowLeft className="w-4 h-4" />
              Voltar para o login
            </Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell descricao="Recuperação de senha">
      <form onSubmit={(e) => void handleSubmit(aoEnviar)(e)} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Informe o e-mail da sua conta e enviaremos um link para criar uma nova senha.
        </p>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-brand font-medium">
            E-mail
          </Label>
          <Input id="email" type="email" autoComplete="email" {...register('email')} />
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-brand text-brand-foreground hover:bg-brand/90"
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enviar link
        </Button>

        <Button asChild variant="ghost" className="w-full">
          <Link to={ROTAS.login}>
            <ArrowLeft className="w-4 h-4" />
            Voltar para o login
          </Link>
        </Button>
      </form>
    </AuthShell>
  );
}
